/**
 * Flow Preference Status utilities
 * 
 * Handles the three-state preference model:
 * - 'normal': item is available in the flow (default)
 * - 'preferred': item is available and marked as preferred (starred)
 * - 'notAvailable': item is not available in the flow
 * 
 * Supports expand-contract migration: reads `status` field first,
 * falls back to deriving from legacy `notAvailable` boolean.
 */

import type { FlowRanking, FlowPreferenceStatus } from './models'

/**
 * Resolve the effective status from a FlowRanking, supporting
 * both the new `status` field and the legacy `notAvailable` boolean.
 */
export function resolveFlowPreferenceStatus(ranking: FlowRanking | undefined): FlowPreferenceStatus {
  if (!ranking) return 'normal'
  if (ranking.status) return ranking.status
  // Legacy fallback: derive from notAvailable boolean
  return ranking.notAvailable ? 'notAvailable' : 'normal'
}

/**
 * Cycle through preference states: normal → preferred → notAvailable → normal
 */
export function cycleFlowPreferenceStatus(current: FlowPreferenceStatus): FlowPreferenceStatus {
  switch (current) {
    case 'normal': return 'preferred'
    case 'preferred': return 'notAvailable'
    case 'notAvailable': return 'normal'
  }
}

/**
 * Check if a ranking represents an available item (normal or preferred)
 */
export function isAvailableStatus(status: FlowPreferenceStatus): boolean {
  return status === 'normal' || status === 'preferred'
}

/**
 * Create a FlowRanking with both new status and legacy notAvailable for backward compat
 */
export function createFlowRankingWithStatus(
  rank: number,
  status: FlowPreferenceStatus,
  name: string
): FlowRanking {
  return {
    r: rank,
    n: name,
    notAvailable: status === 'notAvailable',
    status
  }
}

/**
 * Compute which flows have available status (normal or preferred) for a given item.
 */
export function getAvailableFlowsForItem<T extends { id: string; rankings: Record<string, FlowRanking | undefined> }>(itemId: string, flows: T[]): T[] {
  return flows.filter(f => {
    const status = resolveFlowPreferenceStatus(f?.rankings[itemId])
    return isAvailableStatus(status)
  })
}
